// Referral attribution for the POST /user call. A share link looks
// like https://diffit.me/r/<code> (see the /r/:code route, which rewrites it to
// `?ref=<code>` and redirects home) and `?ref=<code>` is also accepted on any URL.
// Email invites carry `?i=<referral_id>` plus `?ref=<code>` as a fallback.
//
// The click and the POST /user that carries the code can be days or weeks apart —
// sessionStorage (see connectorReferral.js) would not survive that gap — so this persists to
// localStorage with a captured-at timestamp and expires client-side to mirror the backend's
// 90-day attribution window. Drained by POST /user, which attributes on every sign-in and
// not only on account creation, so a teacher who was already signed in when they followed
// the link is attributed too; whoever drains it calls clearReferralAttribution, without
// which the record would be re-sent on every load for 90 days.
//
// Every storage access is wrapped in try/catch: storage throws in private-mode/blocked-
// cookie browsers, and that must never break sign-in.

const STORAGE_KEY = 'diffitReferralAttribution';
const ATTRIBUTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000; // 90 days, mirrors the backend window

// Reads `ref` / `i` off the current URL and persists whichever is present, along with the
// `ref_src` naming the surface the link came off. Safe to call on any page load: when
// neither `ref` nor `i` is present this is a no-op that leaves any previously stored
// attribution alone, rather than clobbering it with an empty capture — `ref_src` alone is
// not a capture, it only ever qualifies a code.
// Takes `search` rather than reading window.location.search directly: routes whose
// signed-out branch is `<Start redirect />` (`= <Navigate to='/' replace />`, e.g. `/referral`,
// `/eval`) drop the query string, and `<Navigate>` is itself a useEffect — React runs child
// effects before parent ones, so its synchronous replaceState has already blanked
// window.location.search by the time Base's capture effect runs. React Router's
// location.search is the value parsed at Base's own render, so it is immune to that.
export function captureReferralFromUrl(search = window.location.search) {
  try {
    const params = new URLSearchParams(search);
    const referralId = params.get('i');
    const code = params.get('ref');

    if (!referralId && !code) return null;

    const linkSource = params.get('ref_src');

    // Keep both. An invite link carries `i` AND `ref` on purpose: if the invite row is
    // already claimed (someone else signed up through a forwarded copy), the backend falls
    // back to the code and this recipient still counts. Replace the whole record rather
    // than merging — the two params always come from one referrer, and last touch wins, so
    // merging could pair one referrer's invite id with another's code.
    const record = {
      code: code || null,
      referralId: referralId || null,
      linkSource: linkSource || null,
      capturedAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch {
    return null;
  }
}

// Returns { referral_code, referral_id, referral_link_source } for the POST /user body, or
// null if nothing is stored, the stored blob is malformed, or the capture has aged out of
// the attribution window.
export function readReferralAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const { code, referralId, linkSource, capturedAt } = JSON.parse(raw);
    if (!code && !referralId) return null;
    if (typeof capturedAt !== 'number' || Date.now() - capturedAt > ATTRIBUTION_WINDOW_MS) {
      // Aged out, and no later read can revive it — drop the key rather than leaving dead
      // data in storage for the rest of the browser profile's life.
      clearReferralAttribution();
      return null;
    }

    return {
      referral_code: code || null,
      referral_id: referralId || null,
      referral_link_source: linkSource || null,
    };
  } catch {
    return null;
  }
}

export function readUnwelcomedReferralCode() {
  try {
    const stored = readReferralAttribution();
    if (!stored?.referral_code) return null;

    const { welcomedAt } = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return welcomedAt ? null : stored.referral_code;
  } catch {
    return null;
  }
}

export function markReferralWelcomed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...JSON.parse(raw), welcomedAt: Date.now() })
    );
  } catch {
    // Private mode / blocked cookies. Nothing persisted, so the welcome may show again —
    // strictly better than letting a storage failure break the page.
  }
}

// Forget the stored capture. Call this once the record has been handed to POST /user —
// deliberately on a successful *request*, not on a successful attribution: the backend
// answers a rejected code (an account that isn't dormant enough, a self-referral, one
// already referred) with the same 200 as an accepted one, and none of those become
// eligible by being retried. A network or 5xx failure leaves the record in place so the
// next load tries again.
export function clearReferralAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked cookies — nothing was stored, so nothing to forget.
  }
}
